// ABOUTME: The /applications route, the Jobs destination of the bottom navigation: the applications block for talents
// ABOUTME: in the PRO_ATS tracker arm, and the app's not-found screen for everyone else, matching the backend's 404.
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import { PageHeader } from 'components/PageHeader';
import { NotFoundErrorScreen } from 'components/screens';
import { Text } from 'components/Text';
import { Applications, useTrackerArm } from 'modules/applications';
import { JobsFeed } from 'modules/feed';
import { getTrans } from 'modules/i18n';
import { JobFeedGate, useListingFeedEnabled } from 'modules/jobListings';
import { Layout } from 'modules/minimal-ui-theme';

/**
 * "My applications", for talents in the tracker arm of PRO_ATS only. A control talent, or anyone the proxy
 * did not put in an arm, gets the app's not-found screen: for them the route does not exist, exactly as the backend
 * answers 404 for them.
 */
export const ApplicationsPage = () => {
  const { variant, isTracker, isLoading } = useTrackerArm();
  // Set by the apply flow on its way back here; it lives in the history entry, so a reload or the next navigation clears it.
  const location = useLocation();
  const navigate = useNavigate();
  // Set by the apply flow on its way here. Read once into state and taken out of the history entry, so the banner
  // shows for this visit and not again when the talent comes back to this entry later.
  const [applicationSent] = useState(
    () =>
      (location.state as { applicationSent?: string } | null)?.applicationSent
  );
  useEffect(() => {
    if (
      (location.state as { applicationSent?: string } | null)?.applicationSent
    ) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  if (isLoading) return null;

  if (!isTracker) {
    return <NotFoundErrorScreen />;
  }

  // A destination of the bottom navigation, not a drill-in: the wordmark bar on top and the page's own heading, the
  // way the feed is drawn, rather than a back arrow that would lead out of a top-level screen.
  return (
    <Layout header={<PageHeader />}>
      <Text
        variant="h4"
        component="h1"
        color="text.primary"
        sx={{ fontWeight: 700, mb: 2 }}
      >
        {getTrans('applicationsPage.title')}
      </Text>
      {applicationSent && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 3 }}>
          {getTrans('applications.sent')}
        </Alert>
      )}
      <JobsContent variant={variant} />
    </Layout>
  );
};

/**
 * PJL: with PRO_LISTING_FEED on, the page is pro-core's feed of sections. Off, or answered 404 by pro-core, it is
 * today's page: the applications block and the job feed under it, each asking for itself.
 */
const JobsContent = ({ variant }: { variant: string }) => {
  const { isEnabled } = useListingFeedEnabled();
  const today = (
    <>
      <Applications variant={variant} />
      <JobFeedGate />
    </>
  );

  // Until the flag says yes (also while the proxy has not answered) the page is exactly today's.
  return isEnabled ? <JobsFeed fallback={today} /> : today;
};
